import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TablePagination,
  TextField,
  Checkbox,
  FormControlLabel,
  DialogTitle,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import { OperationalDaysClient, OperationalDayTemplateDto } from '../../app/services/api/tasksClient';

const operationalDaysClient = OperationalDaysClient;

const OperationalDaysPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['OperationalDaysView']) : false;
  const canCreate = hasPermissionTo ? hasPermissionTo(['OperationalDaysCreate']) : false;
  const canDelete = hasPermissionTo ? hasPermissionTo(['OperationalDaysDelete']) : false;

  const [days, setDays] = useState<OperationalDayTemplateDto[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchActiveOnly, setSearchActiveOnly] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteTarget, setDeleteTarget] = useState<OperationalDayTemplateDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!canView) return;
    operationalDaysClient
      .getOperationalDays(searchActiveOnly ? true : undefined)
      .then(r => setDays((r).data ?? r))
      .catch(() => setDays([]));
  }, [canView, searchActiveOnly]);

  const filteredDays = useMemo(() => {
    return days.filter(d => {
      if (searchActiveOnly && !d.isActive) return false;
      if (searchText.trim()) {
        const text = searchText.toLowerCase();
        const name = (d.name ?? '').toLowerCase();
        const description = (d.description ?? '').toLowerCase();
        if (!name.includes(text) && !description.includes(text)) return false;
      }
      return true;
    });
  }, [days, searchActiveOnly, searchText]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedDays = useMemo(
    () => filteredDays.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredDays, page, rowsPerPage],
  );

  const handleOpenCreate = () => {
    if (!canCreate) return;
    navigate('/operational-days/new');
  };

  const handleOpenEdit = (row: OperationalDayTemplateDto) => {
    if (!canView) return;
    navigate(`/operational-days/${row.id}`);
  };

  const handleRequestDelete = (row: OperationalDayTemplateDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDelete) return;
    setDeleteTarget(row);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await operationalDaysClient.deleteOperationalDay(deleteTarget.id);
      setDays(prev => prev.filter(d => d.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmOpen(false);
  };

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>Operational Days</PageTitle>
        <Pill text='You do not have permission to view operational days.' />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle>Operational Days</PageTitle>
        </Grid>

        <Grid item
          xs={12}
          md={9}>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={4}>
              <TextField
                label='Search'
                fullWidth
                size='small'
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder='Search by name or description'
              />
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={searchActiveOnly}
                    onChange={e => setSearchActiveOnly(e.target.checked)}
                  />
                }
                label='Active only'
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={3}
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
          {canCreate && (
            <Button
              size='lg'
              variant='primary'
              startIcon={<Plus />}
              onClick={handleOpenCreate}
            >
              Create Operational Day
            </Button>
          )}
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>Operational Days</TableHeader>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Effective from</TableCell>
                    <TableCell>Effective to</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDays.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleOpenEdit(row)}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{row.effectiveFromDate?.substring(0, 10)}</TableCell>
                      <TableCell>{row.effectiveToDate?.substring(0, 10)}</TableCell>
                      <TableCell align='right'
                        onClick={e => e.stopPropagation()}>
                        {canDelete && (
                          <DeleteOutlineIcon
                            fontSize='small'
                            color='error'
                            onClick={e => handleRequestDelete(row, e)}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedDays.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}
                        style={{ textAlign: 'center' }}>
                        No operational days found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      count={filteredDays.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>

      {deleteTarget && (
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
        >
          <DialogTitle>Delete operational day?</DialogTitle>
          <DialogContent>
            {`Are you sure you want to delete the operational day "${deleteTarget.name}"? This action cannot be undone.`}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              color='error'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </PageArea>
  );
};

export default OperationalDaysPage;
